// «Область интереса» — pure helpers for the heatmap IN/OUT region. No Angular here;
// twitch-chat-state.service.ts owns the signals, tools/test-region.mjs tests this file.
import type { ActivityBin, Highlight } from '../models/twitch.model';

export interface TimeRegion {
  inSec: number;   // inclusive, multiple of REGION_SNAP_SEC
  outSec: number;  // exclusive, multiple of REGION_SNAP_SEC
}

// Edges live on the 15s heatmap bin grid: a bin is either inside or outside, and Deep /
// AI search can rebase bins by `inSec` without breaking their `startTime += 15` walk.
export const REGION_SNAP_SEC = 15;
export const MIN_REGION_SEC = 60;

const snapDown = (t: number) => Math.floor(t / REGION_SNAP_SEC) * REGION_SNAP_SEC;
const snapUp = (t: number) => Math.ceil(t / REGION_SNAP_SEC) * REGION_SNAP_SEC;
const snapNearest = (t: number) => Math.round(t / REGION_SNAP_SEC) * REGION_SNAP_SEC;
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/**
 * Does [start, end] touch [inSec, outSec)? `end` is treated as inclusive because peak /
 * Deep highlights store the START of their last bin there (a one-bin peak has
 * start === end) — so a peak whose last bin is the region's first bin counts as inside.
 */
export function overlapsRegion(start: number, end: number, r: TimeRegion): boolean {
  return start < r.outSec && Math.max(start, end) >= r.inSec;
}

/** Display rule: with a region active only moments touching it are shown — except pinned («Избранное»). */
export function isVisibleInRegion(
  h: Pick<Highlight, 'startTime' | 'endTime' | 'isPinned'>,
  r: TimeRegion | null,
): boolean {
  return !r || !!h.isPinned || overlapsRegion(h.startTime, h.endTime, r);
}

/**
 * Normalize to the bin grid inside [0, maxSec] with at least MIN_REGION_SEC length.
 * null when the stream itself is shorter than the minimum (nothing sensible to select).
 */
export function clampRegion(r: TimeRegion, maxSec: number): TimeRegion | null {
  const max = snapUp(maxSec);
  if (!(max >= MIN_REGION_SEC)) return null;
  const inSec = clamp(snapNearest(r.inSec), 0, max - MIN_REGION_SEC);
  const outSec = clamp(snapNearest(r.outSec), inSec + MIN_REGION_SEC, max);
  return { inSec, outSec };
}

/** Drag one edge to `t` (snapped to the nearest bin edge), never past the other edge − minimum. */
export function moveRegionEdge(r: TimeRegion, edge: 'in' | 'out', t: number, maxSec: number): TimeRegion {
  const max = snapUp(maxSec);
  const s = snapNearest(t);
  if (edge === 'in') return { inSec: clamp(s, 0, Math.max(0, r.outSec - MIN_REGION_SEC)), outSec: r.outSec };
  return { inSec: r.inSec, outSec: clamp(s, r.inSec + MIN_REGION_SEC, Math.max(max, r.inSec + MIN_REGION_SEC)) };
}

/**
 * Alt+I / Alt+O: put one edge at the playhead (IN rounds down, OUT rounds up — the bin
 * under the playhead stays inside). The other edge is kept when it still leaves the
 * minimum length, otherwise it falls back to the stream start / end.
 */
export function setRegionEdgeAt(
  r: TimeRegion | null,
  edge: 'in' | 'out',
  t: number,
  maxSec: number,
): TimeRegion | null {
  const max = snapUp(maxSec);
  if (!(max >= MIN_REGION_SEC)) return null;
  if (edge === 'in') {
    const inSec = clamp(snapDown(t), 0, max - MIN_REGION_SEC);
    const outSec = r && r.outSec >= inSec + MIN_REGION_SEC ? Math.min(r.outSec, max) : max;
    return { inSec, outSec };
  }
  const outSec = clamp(snapUp(t), MIN_REGION_SEC, max);
  const inSec = r && r.inSec <= outSec - MIN_REGION_SEC ? r.inSec : 0;
  return { inSec, outSec };
}

/** Restore guard for SavedState — anything malformed reads as «no region». */
export function parseRegion(x: unknown): TimeRegion | null {
  if (!x || typeof x !== 'object') return null;
  const { inSec, outSec } = x as Record<string, unknown>;
  if (typeof inSec !== 'number' || typeof outSec !== 'number') return null;
  if (!Number.isFinite(inSec) || !Number.isFinite(outSec) || inSec < 0 || outSec <= inSec) return null;
  return { inSec, outSec };
}

export function binsInRegion<T extends Pick<ActivityBin, 'startTime'>>(bins: T[], r: TimeRegion): T[] {
  return bins.filter(b => b.startTime >= r.inSec && b.startTime < r.outSec);
}

/**
 * Shift bins so the region starts at 0. The Deep / AI-search servers derive the stream
 * length (cap, per-hour sweep, «Длительность стрима») from the max bin startTime — sent
 * with absolute times, a 30-min window at 5:00 would be described as a 6-hour stream.
 */
export function rebaseBins<T extends Pick<ActivityBin, 'startTime'>>(bins: T[], base: number): T[] {
  return bins.map(b => ({ ...b, startTime: b.startTime - base }));
}

export function formatClock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

export function formatRegionDuration(seconds: number): string {
  const totalMin = Math.round(seconds / 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m} мин`;
  return m === 0 ? `${h} ч` : `${h} ч ${m} мин`;
}

/** «01:00:00–01:30:00» */
export function formatRegion(r: TimeRegion): string {
  return `${formatClock(r.inSec)}–${formatClock(r.outSec)}`;
}
